import { Container, Grid } from '@mui/material'
import type { FC } from 'react'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import ErrorPage from '@/components/UI/ErrorPage'
import AppBarPageContainer from '@/components/UI/PageContainer'
import Tabs from '@/components/UI/Tabs'
import useTab from '@/hooks/useTab'
import useUser from '@/hooks/useUser'
import { getExecutiveQuery } from '@/lib/apollo'
import type { TransformedExecutiveData } from '@/lib/apollo/query/getExecutive'
import { Role } from '@/types/User'
import { hasRole } from '@/utils/role'
import type { Squad } from '@/utils/squad'
import { defaultGridSpacing } from '@/utils/themes'

import ActivityTab from './ActivityTab'
import AdminSettingsTab from './AdminSettingsTab'
import AppBarContent from './AppBarContent'
import CompletedTab from './CompletedTab'

type OnUpdateExecutiveArgs = { squads: Squad[] }

export type Props = {
  collapsedMenu: boolean
  onUpdateExecutive: ({ squads }: OnUpdateExecutiveArgs) => void
}

type Params = {
  userId: string
}

const tabs = ['completed', 'activity', 'admin-settings'] as const

type Tab = (typeof tabs)[number]

const ExecutivePage: FC<Props> = ({ collapsedMenu, onUpdateExecutive }) => {
  const { userId } = useParams<Params>()

  const { selectedTab, handleChangeTab } = useTab<Tab>({
    availableTabs: tabs,
    defaultTab: 'completed',
  })

  const { roles } = useUser()

  const [executiveData, setExecutiveData] = useState<
    TransformedExecutiveData | undefined
  >(undefined)

  const [fetch, { loading, error }] = getExecutiveQuery.useLazyQuery()

  useEffect(() => {
    if (!userId) {
      return
    }

    fetch({
      variables: {
        userId,
      },
    })
      .then(({ data }) => {
        setExecutiveData(getExecutiveQuery.toTransformedData(data))
      })
      .catch(() => {})
  }, [fetch, userId])

  const hasSettingsTabPermission = hasRole(roles, [Role.admin, Role.supervisor])

  return error || !userId ? (
    <ErrorPage />
  ) : (
    <AppBarPageContainer
      loading={loading || executiveData === undefined}
      isNavigationDrawerOpen={!collapsedMenu}
      headerAppBarContent={
        <AppBarContent
          loading={loading || executiveData === undefined}
          fullName={executiveData?.fullName}
          skillLevel={executiveData?.skillLevel}
          squads={executiveData?.squads}
        />
      }
      pageContent={
        <Container maxWidth="xl">
          <Grid container spacing={defaultGridSpacing} paddingBottom={defaultGridSpacing}>
            <Grid item xs={12}>
              <Tabs<Tab>
                value={selectedTab}
                onChange={handleChangeTab}
                config={[
                  {
                    label: 'Completed',
                    value: 'completed' as Tab,
                  },
                  {
                    label: 'Activity',
                    value: 'activity',
                  },

                  ...(hasSettingsTabPermission
                    ? [
                        {
                          label: 'Admin Settings',
                          value: 'admin-settings' as Tab,
                        },
                      ]
                    : []),
                ]}
              />
            </Grid>
            <Grid item xs={12}>
              {executiveData === undefined ? null : selectedTab === 'admin-settings' ? (
                <AdminSettingsTab
                  userId={userId}
                  squads={executiveData.squads ?? []}
                  skillLevel={executiveData.skillLevel}
                  testId="admin-settings-tab-content"
                  onUpdateExecutive={(skillLevel, squads) => {
                    if (executiveData !== undefined) {
                      setExecutiveData({
                        ...executiveData,
                        skillLevel,
                        squads,
                      })

                      onUpdateExecutive({ squads })
                    }
                  }}
                />
              ) : selectedTab === 'completed' ? (
                <CompletedTab
                  userId={userId}
                  testId="completed-tab-content"
                  email={executiveData.primaryEmail}
                />
              ) : (
                <ActivityTab
                  userId={userId}
                  testId="activity-tab-content"
                  email={executiveData.primaryEmail}
                />
              )}
            </Grid>
          </Grid>
        </Container>
      }
    />
  )
}

export default ExecutivePage
